// The only QR surface: opened from the row's QR button. It was briefly a panel tab as
// well; sharing QR codes is repeated across many workflows in a row, so it belongs on
// the row rather than behind a panel open.

import { useRef } from 'react';
import { Button, Center, HStack, Image, Text, VStack, useToast } from '@chakra-ui/react';
import { MdContentCopy, MdDownload, MdPrint } from 'react-icons/md';
import { QRCodeSVG } from 'qrcode.react';
import { useReactToPrint } from 'react-to-print';
import { Modal } from '@/design-system/components/Modal';
import { Chatbot } from '@/lib/sdk/chatbot/types';
import { deepLink, startCommand } from './flowState';

interface WorkflowQrModalProps {
  chatbot: Chatbot | null;
  onClose: () => void;
}

export function WorkflowQrModal({ chatbot, onClose }: WorkflowQrModalProps) {
  const toast = useToast();
  const printRef = useRef<HTMLDivElement>(null);
  const print = useReactToPrint({ contentRef: printRef });

  const handleCopy = () => {
    if (!chatbot) return;
    navigator.clipboard.writeText(deepLink(chatbot));
    toast({ title: 'Copied to clipboard!', status: 'success', duration: 3000 });
  };

  const handleDownload = () => {
    if (chatbot?.qr_code?.qr_image_url) {
      window.open(chatbot.qr_code.qr_image_url, '_blank');
    }
  };

  return (
    <Modal
      isOpen={chatbot !== null}
      onClose={onClose}
      title="WhatsApp QR & link"
      size="md"
      testId="wfl-qr-modal"
      footer={
        <HStack>
          <Button
            variant="ghost"
            leftIcon={<MdContentCopy />}
            onClick={handleCopy}
            data-testid="wfl-qr-copy-btn"
          >
            Copy link
          </Button>
          <Button
            variant="ghost"
            leftIcon={<MdPrint />}
            onClick={() => print()}
            data-testid="wfl-qr-print-btn"
          >
            Print card
          </Button>
          <Button
            colorScheme="brand"
            leftIcon={<MdDownload />}
            onClick={handleDownload}
            isDisabled={!chatbot?.qr_code?.qr_image_url}
            data-testid="wfl-qr-download-btn"
          >
            Download QR
          </Button>
        </HStack>
      }
    >
      {chatbot && (
        <VStack ref={printRef} spacing={3} py={2}>
          <Center bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="border-muted">
            {chatbot.qr_code?.qr_image_url ? (
              <Image
                src={chatbot.qr_code.qr_image_url}
                alt={`QR code for ${chatbot.title}`}
                boxSize="200px"
                objectFit="contain"
                fallback={
                  <QRCodeSVG
                    value={deepLink(chatbot)}
                    size={200}
                    level="H"
                    fgColor="var(--chakra-colors-brand-500)"
                  />
                }
              />
            ) : (
              <QRCodeSVG
                value={deepLink(chatbot)}
                size={200}
                level="H"
                fgColor="var(--chakra-colors-brand-500)"
              />
            )}
          </Center>
          <VStack spacing={1}>
            <Text data-testid="wfl-qr-title" fontWeight="bold" color="text-primary">
              {chatbot.title}
            </Text>
            <Text
              data-testid="wfl-qr-command"
              fontFamily="mono"
              fontSize="sm"
              color="text-secondary"
            >
              {startCommand(chatbot.short_code)}
            </Text>
          </VStack>
        </VStack>
      )}
    </Modal>
  );
}
